import { Injectable, inject } from '@angular/core';
import { HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';

import { BehaviorSubject, Observable, switchMap, filter, first } from 'rxjs';

import { AccountService } from '../auth/services/account.service';
import { LocalStorageService } from '../shared/services/local-storage.service';

// BOILERPLATE NOTE: This class requires to be a singleton, so the refreshing state is the same
// for all potential instances of it
@Injectable({ providedIn: 'root' })
export class RefreshTokenService {
  localStorageService = inject(LocalStorageService);
  accountService = inject(AccountService);
  private isRefreshing = false;
  private refreshToken$ = new BehaviorSubject<string | null>(null);

  handleExpiredTokenError(req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
    // In case requests are sent while the token is being refreshed, we avoid concurrency problems
    // making sure that while one request refreshes the token, the rest wait
    return !this.isRefreshing ? this.refreshToken(req, next) : this.waitForTokenToBeRefreshed(req, next);
  }

  private refreshToken(req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
    this.isRefreshing = true;
    const refreshToken = this.localStorageService.token?.refreshToken as string;
    return this.accountService.refreshToken(refreshToken).pipe(
      switchMap((token) => {
        this.isRefreshing = false;
        this.localStorageService.token = token;
        this.refreshToken$.next(token.key);
        return next(req);
      }),
    );
  }

  private waitForTokenToBeRefreshed(req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
    return this.refreshToken$.pipe(
      filter((token) => token !== null),
      first(), // unsubscribe once token is refreshed
      switchMap(() => next(req)),
    );
  }
}
